import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ConfirmDialog,
  type ContextMenuItem,
  SwatchCard,
  toast,
} from '@core/components';
import { useConfirmDialog } from '@core/hooks/useConfirmDialog';
import { useCrudList } from '@core/hooks/useCrudList';
import {
  type MaterialColor,
  deleteMaterialColor,
  reorderMaterialColors,
} from '../../../api/materialColor';
import {
  type MaterialType,
  deleteMaterialType,
  getMaterialTypes,
  reorderMaterialTypes,
} from '../../../api/materialType';
import GroupedSortableSettings, {
  useSelectedGroup,
} from '../../components/GroupedSortableSettings';
import MaterialColorDialog from '../../components/MaterialColorDialog';
import MaterialTypeDialog from '../../components/MaterialTypeDialog';
import { useAppContext } from '../../context/AppContext';
import type { MaterialKind } from '../../enum/materialKind';

const SUMMARY_DOT_LIMIT = 8;

function colorCountLabel(count: number): string {
  if (count === 0) {
    return 'No colors';
  }
  return `${count} color${count === 1 ? '' : 's'}`;
}

function ColorSummary({ colors }: { colors: MaterialColor[] }) {
  if (colors.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" noWrap>
        No colors
      </Typography>
    );
  }

  const hiddenCount = colors.length - SUMMARY_DOT_LIMIT;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {colors.slice(0, SUMMARY_DOT_LIMIT).map((color) => (
        <Box
          key={color.id}
          sx={{
            width: 10,
            height: 10,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: color.hexCode,
            border: 1,
            borderColor: 'divider',
          }}
        />
      ))}
      {hiddenCount > 0 ? (
        <Typography variant="caption" color="text.secondary" noWrap>
          +{hiddenCount}
        </Typography>
      ) : null}
    </Box>
  );
}

export interface MaterialSettingsProps {
  kind: MaterialKind;
  labels: { singular: string; plural: string };
}

const MaterialSettings = ({ kind, labels }: MaterialSettingsProps) => {
  const { refreshContext } = useAppContext();
  const [colorDialog, setColorDialog] = useState<{
    open: boolean;
    color: MaterialColor | null;
  }>({ open: false, color: null });

  const typeLabel = `${labels.singular} Type`;

  const loadTypes = useCallback(() => getMaterialTypes(kind), [kind]);
  const reorderTypes = useCallback(
    (ids: number[]) => reorderMaterialTypes(kind, ids),
    [kind]
  );

  const {
    items: types,
    setItems: setTypes,
    loading,
    load,
    dialogOpen,
    editingItem: editingType,
    openCreate,
    openEdit,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    rowReorder,
  } = useCrudList<MaterialType>({
    loadItems: loadTypes,
    deleteItem: (type) => deleteMaterialType(type.id),
    reorderItems: reorderTypes,
    loadErrorMessage: `Failed to load ${labels.plural.toLowerCase()}`,
    deleteSuccessMessage: `${typeLabel} deleted`,
    deleteErrorMessage: `Failed to delete ${typeLabel.toLowerCase()}`,
    reorderErrorMessage: 'Failed to reorder types',
    refreshContext,
  });

  const { selectedGroup: selectedType, selectGroup: selectType } =
    useSelectedGroup(types, 'type');

  const typeMenuItems = (type: MaterialType): ContextMenuItem[] => [
    { label: 'Rename', onClick: () => openEdit(type) },
    {
      label: 'Delete',
      destructive: true,
      onClick: () => requestDelete(type),
    },
  ];

  const typeDeleteContent =
    deleteConfirm && deleteConfirm.colors.length > 0
      ? `Deleting "${deleteConfirm.name}" also deletes its ${colorCountLabel(deleteConfirm.colors.length).toLowerCase()}.`
      : `Are you sure you want to delete "${deleteConfirm?.name}"?`;

  const colorDelete = useConfirmDialog<MaterialColor>({
    onConfirm: (color) => deleteMaterialColor(color.id),
    onSuccess: load,
    successMessage: 'Color deleted',
    errorMessage: 'Failed to delete color',
  });

  const colorMenuItems = (color: MaterialColor): ContextMenuItem[] => [
    { label: 'Edit', onClick: () => setColorDialog({ open: true, color }) },
    {
      label: 'Delete',
      destructive: true,
      onClick: () => colorDelete.request(color),
    },
  ];

  const onColorsReorder = (type: MaterialType, next: MaterialColor[]) => {
    setTypes((prev) =>
      prev.map((item) =>
        item.id === type.id ? { ...item, colors: next } : item
      )
    );
    reorderMaterialColors(
      type.id,
      next.map((color) => color.id)
    )
      .then(refreshContext)
      .catch(() => {
        toast.error('Failed to reorder colors');
        void load();
      });
  };

  return (
    <GroupedSortableSettings<MaterialType, MaterialColor>
      title={labels.plural}
      loading={loading}
      groups={types}
      selectedGroup={selectedType}
      onSelectGroup={selectType}
      groupReorder={rowReorder}
      groupLabel="Type"
      renderGroupSecondary={(type) => <ColorSummary colors={type.colors} />}
      getGroupMenuItems={typeMenuItems}
      addGroup={{ label: `Add ${typeLabel}`, onClick: openCreate }}
      emptyState={{
        title: `No ${labels.singular.toLowerCase()} types yet`,
        description: 'Add a type first, then add its colors.',
      }}
      getItems={(type) => type.colors}
      getItemsCaption={(type, count) =>
        count === 0
          ? `No colors yet. Add the first ${type.name} color.`
          : colorCountLabel(count)
      }
      onItemsReorder={onColorsReorder}
      renderItem={(color, onMenuClick) => (
        <SwatchCard
          color={color.hexCode}
          title={color.name}
          onClick={() => setColorDialog({ open: true, color })}
          onMenuClick={onMenuClick}
        />
      )}
      getItemMenuItems={colorMenuItems}
      addItem={{
        label: 'Add color',
        onClick: () => setColorDialog({ open: true, color: null }),
      }}
    >
      <MaterialTypeDialog
        open={dialogOpen}
        onClose={closeDialog}
        kind={kind}
        label={typeLabel}
        materialType={editingType}
        onSuccess={(saved) => {
          void load();
          selectType(saved.id);
        }}
      />
      <ConfirmDialog
        open={deleteConfirm !== null}
        title={`Delete ${typeLabel}`}
        content={typeDeleteContent}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
      {selectedType ? (
        <MaterialColorDialog
          open={colorDialog.open}
          onClose={() => setColorDialog({ open: false, color: null })}
          kind={kind}
          materialType={selectedType}
          color={colorDialog.color}
          onSuccess={() => void load()}
        />
      ) : null}
      <ConfirmDialog
        open={colorDelete.open}
        title="Delete Color"
        content={`Are you sure you want to delete "${colorDelete.item?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={() => void colorDelete.confirm()}
        onCancel={colorDelete.cancel}
        loading={colorDelete.loading}
      />
    </GroupedSortableSettings>
  );
};

export default MaterialSettings;
